// Parser for Skool's post/comment content format into a small, safe token tree
// that SkoolContent.svelte renders with real elements (no innerHTML, so untrusted
// text can't inject markup). The format Skool stores:
//   - links:     [text](https://url)
//   - mentions:  [@Display Name](obj://user/<id>)
//   - bold:      **text**
//   - lists:     a line starting [ul] or [ol:1], items split on [li]
//   - escapes:   \( \) \[ \] \* … → the literal punctuation
//   - newlines:  single \n = soft break, blank line = new paragraph

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'bold'; v: string }
  | { t: 'italic'; v: string }
  | { t: 'code'; v: string }
  | { t: 'link'; v: string; href: string }
  | { t: 'mention'; v: string };

export type Block =
  | { t: 'p'; lines: Inline[][] }
  | { t: 'list'; ordered: boolean; start: number; items: Inline[][] };

// Mention | markdown link (http/https only) | bold | italic. Order matters: mentions
// before links so [@x](obj://…) isn't misread; bold (**) before italic (*).
const INLINE_RE =
  /\[@([^\]]+)\]\(obj:\/\/[^)]*\)|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`/g;

function unescape(s: string): string {
  return s.replace(/\\([\\()[\]*_~`#-])/g, '$1');
}

function tokenizeInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push({ t: 'text', v: unescape(text.slice(last, m.index)) });
    if (m[1] !== undefined) out.push({ t: 'mention', v: m[1] });
    else if (m[2] !== undefined) out.push({ t: 'link', v: unescape(m[2]), href: m[3]! });
    else if (m[4] !== undefined) out.push({ t: 'bold', v: unescape(m[4]) });
    else if (m[5] !== undefined) out.push({ t: 'italic', v: unescape(m[5]) });
    else if (m[6] !== undefined) out.push({ t: 'code', v: m[6] });
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) out.push({ t: 'text', v: unescape(text.slice(last)) });
  return out;
}

const LIST_RE = /^\[(ul|ol)(?::(\d+))?\]([\s\S]*)$/;       // Skool one-line list
const MD_UL_RE = /^\s*[-*]\s+(.+)$/;                        // markdown "- item" / "* item"
const MD_OL_RE = /^\s*(\d+)\.\s+(.+)$/;                     // markdown "1. item"

type OpenList = { ordered: boolean; start: number; items: Inline[][] };

export function parseSkoolContent(raw: string | null | undefined): Block[] {
  const lines = (raw ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: Inline[][] = [];
  let list: OpenList | null = null;
  const flushPara = (): void => {
    if (para.length) blocks.push({ t: 'p', lines: para });
    para = [];
  };
  const flushList = (): void => {
    if (list) blocks.push({ t: 'list', ordered: list.ordered, start: list.start, items: list.items });
    list = null;
  };
  for (const line of lines) {
    // Skool's own single-line list markup ([ul]/[ol:1] with [li] separators).
    const lm = LIST_RE.exec(line);
    if (lm) {
      flushPara();
      flushList();
      const items = lm[3]!.split('[li]').map((s) => s.trim()).filter(Boolean).map(tokenizeInline);
      if (items.length) blocks.push({ t: 'list', ordered: lm[1] === 'ol', start: lm[2] ? parseInt(lm[2], 10) : 1, items });
      continue;
    }
    // Standard-markdown list lines (AI answers use these) — consecutive lines of the
    // same kind merge into one list; a different kind or a gap starts a new one.
    const ul = MD_UL_RE.exec(line);
    const ol = ul ? null : MD_OL_RE.exec(line);
    if (ul || ol) {
      flushPara();
      const ordered = ol != null;
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, start: ol ? parseInt(ol[1]!, 10) : 1, items: [] };
      }
      list.items.push(tokenizeInline((ul ? ul[1] : ol![2])!));
      continue;
    }
    if (line.trim() === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(tokenizeInline(line));
    }
  }
  flushPara();
  flushList();
  return blocks;
}

// Inline images embedded in the body markup: Skool `![alt](url)` or a bare image URL.
const IMG_MD = /!\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/gi;
const IMG_BARE = /(?:^|\s)(https?:\/\/[^\s)]+\.(?:png|jpe?g|gif|webp)(?:\?[^\s)]*)?)/gi;

/** Image URLs referenced in the body (deduped, in order). */
export function extractImages(raw: string | null | undefined): string[] {
  const s = raw ?? '';
  const urls: string[] = [];
  for (const m of s.matchAll(IMG_MD)) urls.push(m[1]!);
  for (const m of s.matchAll(IMG_BARE)) urls.push(m[1]!);
  return [...new Set(urls)];
}

/** Body with markdown image syntax removed, so the text doesn't show raw URLs. */
export function stripImages(raw: string | null | undefined): string {
  return (raw ?? '').replace(IMG_MD, '').trim();
}

// Flatten the markup to clean one-line plain text — for list excerpts and previews.
export function skoolToPlain(raw: string | null | undefined): string {
  return (raw ?? '')
    .replace(/\[@([^\]]+)\]\(obj:\/\/[^)]*\)/g, '@$1')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '$1')
    .replace(/\[(?:ul|ol)(?::\d+)?\]/g, '')
    .replace(/\[li\]/g, ' · ')
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/\\([\\()[\]*_~`#-])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}
